import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Card,
  Row,
  Col,
  Typography,
  Button,
  Pagination,
  Tooltip,
  Modal,
  message,
  Avatar,
  Spin,
} from "antd";
import {
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  ArrowLeftOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { decryptMessage } from "../../utils/decrypt";
import { getToken } from "../../services/auth";
import { useLocation, useNavigate } from "react-router-dom";
import { CONTEST_MANAGEMENT_BASE, API_BASE_URL } from "../../services/apiConstant";

const { Title } = Typography;

const getStatusTitle = (status_data: string | number) => {
  switch (status_data) {
    case "0":
    case 0:
      return "Draft Contests";
    case "1":
    case 1:
      return "Active Contests";
    case "2":
    case 2:
      return "Upcoming Contests";
    case "3":
    case 3:
      return "Past Contests";
    default:
      return "All Contests";
  }
};

interface Contest {
  id: string;
  title: string;
  createdAt: string;
  status: number;
  StartDate?: string;
  EndDate: string;
  description?: string;
  contestParticipantViewResponse?: Array<any>;
  contestManagementImageResponses?: Array<{
    id: string;
    contestID: string;
    fileName: string;
    fileUrl: string;
  }>;
}

const ContestList: React.FC = () => {
  const navigate = useNavigate();
  const [contests, setContests] = useState<Contest[]>([]);
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(6);
  const location = useLocation();
  const status_data = (location.state as { status: string })?.status || "0";
  const [messageApi, contextHolder] = message.useMessage();
  const [loading, setLoading] = useState<boolean>(true); // ✅ Loading state
  const [deleteModal, setDeleteModal] = useState<{ open: boolean; contestId?: string }>({ open: false });

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are 0-based
    const day = String(date.getDate()).padStart(2, "0");
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };

  const convertToMMDDYYYY = (dateStr: string) => {
    // Remove day-of-week part
    const parts = dateStr.split(",")[1]?.trim(); // "22-10-2025"
    if (!parts) return "";

    const [day, month, year] = parts.split("-");

    return `${month.padStart(2, "0")}/${day.padStart(2, "0")}/${year}`;
  };
  useEffect(() => {
    const fetchContests = async () => {
      setLoading(true); // show loader
      try {
        const token = getToken();
        if (!token) throw new Error("No token found");

        const endpoint = `${API_BASE_URL}${CONTEST_MANAGEMENT_BASE}?Status=${status_data}`;

        const res = await axios.get(endpoint, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const decryptedData = await decryptMessage(res.data.Data, token);
        setContests(decryptedData);
      } catch (error) {
        console.error("Error fetching contests", error);
        messageApi.error("Failed to fetch contests.");
      } finally {
        setLoading(false); // hide loader
      }
    };

    fetchContests();
  }, [status_data]);

  const handleDelete = async (id: string) => {
    try {
      const token = getToken();
      if (!token) throw new Error("No token found");

      await axios.delete(`${API_BASE_URL}${CONTEST_MANAGEMENT_BASE}/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      messageApi.success("Contest deleted successfully!");
      setContests((prev) => prev.filter((c) => c.id !== id));
    } catch (err: any) {
      console.error("Delete error:", err);
      messageApi.error(err.response?.data?.message || "Failed to delete contest.");
    } finally {
      setDeleteModal({ open: false });
    }
  };

  const paginatedContests = contests.slice(
    (page - 1) * pageSize,
    page * pageSize
  );

  return (
    <div>
      {contextHolder}

      <Modal
        title="Are you sure you want to delete this contest?"
        open={deleteModal.open}
        onOk={() => deleteModal.contestId && handleDelete(deleteModal.contestId)}
        onCancel={() => setDeleteModal({ open: false })}
        okText="Yes"
        cancelText="No"
        centered
      />

      <Title level={3} style={{ color: '#41787C' }}>
        <Button
          type="link"
          icon={<ArrowLeftOutlined style={{ fontSize: "18px", fontWeight: "bold", color: "#000", marginRight: 10 }} />}
          onClick={() => navigate(-1)}
          style={{ marginBottom: "10px", padding: 0, color: '#41787C' }}
        />
        {getStatusTitle(status_data)}
      </Title>

      {loading ? ( // ✅ Loader display
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "70vh" }}>
          <img
            src="/images/newLoading.gif" // <-- path to your GIF
            alt="Loading..."
            style={{ width: 200, height: 150 }}
          />
        </div>
      ) : (
        <>
          <Row gutter={[16, 16]}>
            {paginatedContests.length > 0 ? (
              paginatedContests.map((contest) => (
                <Col key={contest.id} xs={24} sm={12} md={8}>
                  <Card
                    hoverable
                    cover={
                      contest.contestManagementImageResponses &&
                        contest.contestManagementImageResponses.length > 0 ? (
                        <img
                          alt={contest.title}
                          src={contest.contestManagementImageResponses[0].fileUrl}
                          loading="lazy"
                          style={{ height: 150, objectFit: "cover" }}
                        />
                      ) : (
                        <div
                          style={{
                            height: 150,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            background: "#f0f0f0",
                            color: "#999",
                          }}
                        >
                          No Image
                        </div>
                      )
                    }
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <Tooltip title={contest.title}>
                        <Title level={5}
                          style={{
                            margin: 0,
                            height: 45, // fixed height for all titles
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            display: "-webkit-box",
                            WebkitLineClamp: 2, // limit to 2 lines
                            WebkitBoxOrient: "vertical",
                          }}
                        >{contest.title}</Title>
                      </Tooltip>

                      <div style={{ display: "flex", gap: "8px" }}>
                        {contest.status === 0 && (
                          <Tooltip title="Edit">
                            <Button
                              type="text"
                              icon={<EditOutlined style={{ color: "#3E767A" }} />}
                              onClick={(e) => { e.stopPropagation(); navigate(`/contest-support/edit`, { state: { contestData: contest, mode: "edit" } }); }}
                            />
                          </Tooltip>
                        )}
                        {(contest.status === 0 || contest.status === 1 || contest.status == 2 || contest.status == 3) && (
                          <Tooltip title="Delete">
                            <Button
                              type="text"
                              icon={<DeleteOutlined style={{ color: "#3E767A", cursor: "pointer" }} />}
                              onClick={(e) => {
                                e.stopPropagation();
                                setDeleteModal({ open: true, contestId: contest.id });
                              }}
                            />
                          </Tooltip>

                        )}

                        {/* Case 1: All statuses except 3 */}
                        {contest.status !== 3 && (
                          <Tooltip title="View">
                            <Button
                              type="text"
                              icon={<EyeOutlined style={{ color: "#3E767A" }} />}
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/contest-support/view`, { state: { contestData: contest, mode: "view" } });
                              }}
                            />
                          </Tooltip>
                        )}

                        {/* Case 2: Status 3 only */}
                        {contest.status === 3 && (
                          <Tooltip title="View (Past)">
                            <Button
                              type="text"
                              icon={<EyeOutlined style={{ color: "#3E767A" }} />}
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/contest-support/view`, { state: { contestData: contest, mode: "view-past" } });
                              }}
                            />
                          </Tooltip>
                        )}
                      </div>
                    </div>

                    {contest.status === 1 ? (
                      <>
                        <Title level={5} style={{ margin: "8px 0 0", fontSize: "14px", color: "#C19763" }}>
                          Active Till: {convertToMMDDYYYY(contest.EndDate)}
                        </Title>
                        <div
                          style={{ display: "flex", alignItems: "center", marginTop: 8, cursor: "pointer", color: "#3E767A" }}
                          onClick={() => navigate(`/participants`, { state: { contestId: contest.id } })}
                        >
                          <Avatar size="small" icon={<UserOutlined />} style={{ marginRight: 8, background: "#3E767A" }} />
                          <span>Participants: {contest.contestParticipantViewResponse?.length || 0}</span>
                        </div>
                      </>
                    ) : (
                      <Title level={5} style={{ margin: "8px 0 0", fontSize: "14px", color: "#C19763" }}>
                        Last Saved: {formatDate(contest.createdAt)}
                      </Title>
                    )}
                  </Card>
                </Col>
              ))
            ) : (
              <p>No contests found.</p>
            )}
          </Row>

          <div style={{ marginTop: 20, textAlign: "center" }}>
            <Pagination
              current={page}
              pageSize={pageSize}
              total={contests.length}
              onChange={(newPage, newPageSize) => { setPage(newPage); setPageSize(newPageSize); }}
              showSizeChanger
            />
          </div>
        </>
      )}
    </div>
  );
};

export default ContestList;



